import React, { useEffect, useState } from 'react';
import { ShotVisual } from './FeatureVisuals';
import { useIsDark } from './motion';
import {
  AlertTriangle, ArrowRight, CheckCircle2, Download as DownloadIcon, HardDrive,
  Loader2, Plug, ShieldCheck, TerminalSquare
} from 'lucide-react';

interface Manifest {
  version: string;
  file: string;
  url: string;
  sha256: string | null;
  sha256_note?: string;
  bytes: number;
  built_at?: string;
  commit: string;
  requires: { node: string };
}

const FALLBACK_FILE = 'agenticscale-hub.tgz';

export const Download: React.FC<{ setActiveTab: (tab: string) => void }> = ({ setActiveTab }) => {
  const dark = useIsDark();
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  // A hub serves this same page. Offering someone a download of the thing they
  // are already running is the sort of small nonsense that makes a product feel
  // like a brochure, so the page asks what it is being served by.
  const [runtime, setRuntime] = useState<string | null>(null);
  const onHub = runtime !== null && /hub/i.test(runtime);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/health')
      .then((response) => (response.ok ? response.json() as Promise<{ runtime?: string }> : Promise.reject(new Error(String(response.status)))))
      .then((health) => { if (!cancelled) setRuntime(health.runtime ?? ''); })
      .catch(() => { if (!cancelled) setRuntime(''); });
    // The version, size and checksum are read from the release that is actually
    // being served, never typed into this page. A checksum that drifts from the
    // file it describes is worse than no checksum.
    fetch('/download/manifest.json')
      .then((response) => (response.ok ? response.json() as Promise<Manifest> : Promise.reject(new Error(String(response.status)))))
      .then((data) => { if (!cancelled) { setManifest(data); setState('ready'); } })
      .catch(() => { if (!cancelled) setState('unavailable'); });
    return () => { cancelled = true; };
  }, []);

  const file = manifest?.file ?? FALLBACK_FILE;
  const folder = file.replace(/\.tgz$/, '');
  const megabytes = manifest ? (manifest.bytes / (1024 * 1024)).toFixed(1) : null;

  return (
    <div className="mx-auto w-full max-w-4xl xl:max-w-5xl 2xl:max-w-6xl space-y-10 py-10">
      {onHub && (
        <div className="rounded-2xl border border-emerald-500/20 bg-emerald-50/60 px-5 py-4 text-sm leading-relaxed text-emerald-900 dark:bg-emerald-500/5 dark:text-emerald-200">
          <strong className="font-bold">You are already running it.</strong> This page is being served by your
          own hub{manifest ? `, release ${manifest.version}` : ''} — the instructions below describe how someone
          else would get here. Your data is in the SQLite file the hub printed when it started.
        </div>
      )}

      <header className="space-y-5 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
          <HardDrive className="h-7 w-7" />
        </div>
        <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white sm:text-4xl">
          Run the whole platform on your own computer
        </h1>
        <p className="mx-auto max-w-2xl text-base leading-relaxed text-slate-600 dark:text-slate-300">
          This site is a demo of software you are meant to run yourself. The download below is the same
          dashboard, the same API, the same policy engine and the same MCP server you see here — with its
          store in a SQLite file you own, bound to your loopback address, with no account and no sign-in.
        </p>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          One archive · No install step · Nothing leaves your machine
        </p>
      </header>

      {/* What arrives, shown rather than listed. Somebody deciding whether to
          download an archive from a site they have just met is deciding on the
          strength of what they can see, and three sentences of prose is not
          it. These are the real screens, in the reader's own theme. */}
      <section className="space-y-4">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          What you get when you run it
        </h2>
        <div className="grid gap-5 sm:grid-cols-3">
          {[
            {
              src: 'window',
              alt: 'The dashboard, listing every agent with its owner, status and limits',
              title: 'Every agent, with its limits',
              body: 'The first screen after you start it, with the sample fleet already loaded so there is something to look at.'
            },
            {
              src: 'decision-block',
              alt: 'A decision panel showing BLOCK with the rules that produced it',
              title: 'A decision, with its reasons',
              body: 'Send an action through and watch it be allowed, held for a person, or refused — with every rule that contributed named.'
            },
            {
              src: 'traces',
              alt: 'Execution traces with an evidence timeline',
              title: 'The evidence, on your disk',
              body: 'Every decision with a SHA-256 of what was decided, in a SQLite file you can open with any SQLite tool.'
            }
          ].map((shot) => (
            <figure key={shot.src} className="flex flex-col gap-3">
              <ShotVisual src={shot.src} alt={shot.alt} dark={dark} height={150} />
              <figcaption>
                <p className="text-sm font-bold text-slate-900 dark:text-white">{shot.title}</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-400">{shot.body}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="glass-panel rounded-3xl p-6 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <DownloadIcon className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Download
            </h2>
          </div>
          {state === 'ready' && manifest && (
            <span className="text-xs font-semibold tabular-nums text-slate-500 dark:text-slate-400">
              v{manifest.version} · {megabytes} MB{manifest.built_at ? ` · built ${manifest.built_at.slice(0, 10)}` : ''}
            </span>
          )}
        </div>

        {state === 'loading' && (
          <p className="mt-4 inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Reading the published release…
          </p>
        )}

        {state === 'unavailable' && (
          <p className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
            The release manifest could not be read, so this page will not print a version or a checksum it
            cannot confirm. Try <code className="rounded bg-amber-100 px-1 dark:bg-amber-500/20">/download/manifest.json</code> directly.
          </p>
        )}

        {state === 'ready' && manifest && !onHub && (
          <>
            <a
              // Not manifest.url: that path is a static asset and Pages serves
              // it without running the Worker, so a download through it is
              // invisible. This records one integer and redirects to the same
              // file. Anyone fetching the static path directly still gets the
              // archive and is simply not counted.
              href="/api/download/hub"
              data-file={manifest.url}
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-brand-500"
            >
              <DownloadIcon className="h-4 w-4" />
              {manifest.file}
            </a>

            <div className="mt-5 space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                SHA-256 — check this before you run it
              </p>
              <p className="break-all font-mono text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                {manifest.sha256}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Built from commit <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">{manifest.commit.slice(0, 7)}</code>{' '}
                by the same pipeline that deployed this page. The archive is built reproducibly, so the same
                commit produces the same bytes and therefore the same checksum.
              </p>
            </div>
          </>
        )}

        {state === 'ready' && manifest && onHub && (
          <div className="mt-5 space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              This hub is release {manifest.version}
            </p>
            <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
              Built from commit{' '}
              <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">{manifest.commit.slice(0, 7)}</code>.
              {' '}A release carries no build timestamp, because a clock inside the archive would change its
              bytes — and its checksum — on every build.
              {' '}A release cannot carry its own checksum — adding it would change the file it describes — so
              the published one lives beside the download on the site.
            </p>
          </div>
        )}

        <pre className="mt-6 overflow-x-auto rounded-2xl bg-slate-900 p-4 text-xs leading-relaxed text-slate-100 dark:bg-slate-950">
{`curl -LO https://agenticscale.org/download/${file}
shasum -a 256 ${file}          # compare with the checksum above
tar xzf ${file}
cd ${folder}
./agenticscale`}
        </pre>

        <p className="mt-4 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          Then open <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs dark:bg-slate-800">http://127.0.0.1:7777</code>.
          The first run creates the database, applies the schema and loads a small sample fleet so the
          dashboard has something in it. On Windows, run <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">agenticscale.cmd</code>.
        </p>

        <p className="mt-4 rounded-xl bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-600 dark:bg-white/5 dark:text-slate-300">
          <strong className="font-bold text-slate-800 dark:text-slate-100">Requires Node.js {(manifest?.requires?.node ?? '22.5').replace(/^[>=^~ ]+/, '')} or newer, and nothing else.</strong>{' '}
          There is no <code className="rounded bg-slate-200 px-1 dark:bg-slate-800">npm install</code>: the API
          is bundled with zero runtime dependencies, and the store is SQLite through Node's own
          <code className="rounded bg-slate-200 px-1 dark:bg-slate-800">node:sqlite</code>, so there is nothing
          to compile either.
        </p>
      </section>

      <section className="glass-panel rounded-3xl p-6 sm:p-8">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            What it does, and what it deliberately does not
          </h2>
        </div>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <ul className="space-y-3">
            {[
              'Runs the same 39 API routes this site runs, verified by the same test suite.',
              'Reaches the same decisions: the specification’s 17 conformance vectors are replayed through the downloaded build on every push.',
              'Keeps every decision, with a SHA-256 of what was decided, in your own SQLite file.',
              'Serves an MCP endpoint your coding agent can call at http://127.0.0.1:7777/api/mcp.'
            ].map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-emerald-600 dark:text-emerald-400" />
                {item}
              </li>
            ))}
          </ul>
          <ul className="space-y-3">
            {[
              'Opens no outbound connection. The test suite records every socket the hub opens and fails the build on any address that is not loopback.',
              'Has no account, no key and no sign-in: reaching a loopback port already means being at the machine.',
              'Sends us nothing once it is running. This site counts the download itself, and learns nothing after that — not that you ran it, nor what you did with it.',
              'Refuses to bind a network address without --allow-remote, and starts read-only when you do.'
            ].map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
                <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-brand-600 dark:text-brand-300" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="glass-panel rounded-3xl p-6 sm:p-8">
        <div className="flex items-center gap-2">
          <TerminalSquare className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Options
          </h2>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <tbody className="divide-y divide-slate-900/5 dark:divide-white/10">
              {[
                ['./agenticscale', 'Dashboard and API on http://127.0.0.1:7777'],
                ['--port 8080', 'Listen somewhere else'],
                ['--data ./here', 'Keep the database in this folder instead of ~/.agenticscale'],
                ['--empty', 'Start with no sample fleet'],
                ['--allow-remote', 'Bind a network address. Read-only: with no login, nothing tells an operator from a visitor'],
                ['--help', 'Everything above, from the tool itself']
              ].map(([flag, description]) => (
                <tr key={flag}>
                  <td className="py-2.5 pr-4 align-top font-mono text-xs text-slate-800 dark:text-slate-100">{flag}</td>
                  <td className="py-2.5 text-slate-600 dark:text-slate-300">{description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
          The database is one file. Delete it to start over; copy it to move your workspace to another
          machine; open it with any SQLite tool to read your own evidence without asking us for an export.
        </p>
      </section>

      <section className="glass-panel rounded-3xl p-6 sm:p-8">
        <div className="flex items-center gap-2">
          <Plug className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Point an agent at it
          </h2>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          The hub serves Model Context Protocol at <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs dark:bg-slate-800">http://127.0.0.1:7777/api/mcp</code>.
          Any MCP client can use it. Three of its tools need no credential and never touch the database: they
          derive a safety profile from a description, validate one, and evaluate an action against one.
        </p>
        <pre className="mt-4 overflow-x-auto rounded-2xl bg-slate-900 p-4 text-xs leading-relaxed text-slate-100 dark:bg-slate-950">
{`{
  "mcpServers": {
    "agenticscale": { "url": "http://127.0.0.1:7777/api/mcp" }
  }
}`}
        </pre>
      </section>

      <section className="glass-panel rounded-3xl border border-amber-500/20 bg-amber-50/50 p-6 dark:bg-amber-500/5 sm:p-8">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-amber-600 dark:text-amber-400" />
          <div className="space-y-2">
            <p className="text-sm font-bold text-slate-900 dark:text-white">The source repository is private</p>
            <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">
              You can download and run the software, but you cannot read its source code. So you cannot check
              that this file was built from the code we describe, or re-run the benchmark yourself.
            </p>
            <button
              type="button"
              onClick={() => setActiveTab('verify')}
              className="inline-flex items-center gap-1 text-xs font-bold text-brand-600 dark:text-brand-300"
            >
              See what else we cannot prove yet <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
